import { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/index';

import type {
  ZuubOnboardingBeginResponse,
  ZuubPayer,
} from '@/@types/zuub/contracts';
import { InfoBanner } from '@/common-components/elements/InfoBanner';
import {
  useZuubRcmOnboardingBegin,
  useZuubRcmOnboardingPayers,
} from '@/react-query/rcm/useZuubRcmOnboarding';

import { ConnectionForm } from './components/ConnectionForm';
import { ConnectionInstructions } from './components/ConnectionInstructions';
import { PayerHeader } from './components/PayerHeader';
import { useZuubOnboardingConnectionStatus } from './hooks/useZuubOnboardingConnectionStatus';
import { getPayerDisplayInfo } from './utils/payerUtils';

interface ConnectToPayerPortalDialogProps {
  payerId: number;
  onboardingResponse?: ZuubOnboardingBeginResponse | null;
  onboardingError?: Error | null;
  isLoadingOnboarding?: boolean;
  isTestingConnection?: boolean;
  isOpen: boolean;
  onIsOpenChange: (isOpen: boolean) => void;
  onCancel?: () => void;
}

export const ConnectToPayerPortalDialog = ({
  payerId,
  onboardingResponse,
  onboardingError = null,
  isLoadingOnboarding = false,
  isTestingConnection = false,
  isOpen,
  onIsOpenChange,
  onCancel,
}: ConnectToPayerPortalDialogProps) => {
  // State for managing the password locally when user manually enters it
  const [localPassword, setLocalPassword] = useState<string>('');
  // Local state to control status polling when parent doesn't
  const [isLocalTestingConnection, setIsLocalTestingConnection] =
    useState<boolean>(false);

  // Get list of payers
  const { data: payers } = useZuubRcmOnboardingPayers();

  // Mutation for updating onboarding data
  const updateOnboardingMutation = useZuubRcmOnboardingBegin();

  // Determine if we should show error state
  const hasError = onboardingError !== null;

  // Get the payer and display info
  const payer = payers?.find((p: ZuubPayer) => p.id === payerId);
  const { payerName, portalUrl, colorCombo, firstTwoLetters } =
    getPayerDisplayInfo(payer);

  // Use the consolidated status hook
  const {
    refetchStatus,
    statusChipProps,
    bannerConfig,
    buttonConfig,
    shouldShowBanner,
    isSucceededStatus,
  } = useZuubOnboardingConnectionStatus({
    payerId,
    isTestingConnection,
    isLocalTestingConnection,
    isLoadingOnboarding,
    hasError,
  });

  // Handle save functionality
  const handleSave = async (password: string) => {
    try {
      await updateOnboardingMutation.mutateAsync({
        payerId,
        password,
      });
      // Store the password locally since the endpoint doesn't return it
      setLocalPassword(password);
    } catch (error) {
      console.error('Failed to update onboarding data:', error);
      // You might want to show an error message to the user here
    }
  };

  // Reset local password when dialog closes
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setLocalPassword('');
      setIsLocalTestingConnection(false);
    }
    onIsOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="vai-max-w-5xl vai-max-h-[90vh] !vai-flex !vai-flex-col vai-overflow-hidden vai-px-20">
        <DialogHeader>
          <DialogTitle className="vai-sr-only">
            <Trans>Connect to</Trans> {payerName} <Trans>Portal</Trans>
          </DialogTitle>
        </DialogHeader>
        {/* Sticky Payer Header */}
        <PayerHeader
          payer={payer}
          colorCombo={colorCombo}
          firstTwoLetters={firstTwoLetters}
          statusChipProps={statusChipProps}
        />
        {/* Scrollable Content */}
        <div className="vai-flex-1 vai-overflow-y-auto">
          {/* Banner at the top */}
          {shouldShowBanner && (
            <div className="vai-min-h-[130px]">
              <InfoBanner
                title={bannerConfig.title}
                description={bannerConfig.description}
                variant={bannerConfig.variant}
                footer={
                  bannerConfig.showFooter ? (
                    bannerConfig.footerAction ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="!vai-p-0"
                        onClick={bannerConfig.footerAction}
                      >
                        {bannerConfig.footerText}
                      </Button>
                    ) : (
                      <span className="vai-text-sm vai-text-red-600">
                        {bannerConfig.footerText}
                      </span>
                    )
                  ) : undefined
                }
              />
            </div>
          )}

          <div className="vai-flex vai-gap-8">
            {/* Left side - Instructions */}
            <ConnectionInstructions
              payerName={payerName}
              portalUrl={portalUrl}
            />

            {/* Right side - Form or Error Message */}
            <ConnectionForm
              onboardingResponse={onboardingResponse}
              onboardingError={onboardingError}
              isLoadingOnboarding={isLoadingOnboarding}
              localPassword={localPassword}
              onSave={handleSave}
              isSaving={updateOnboardingMutation.isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={() => {
              if (isSucceededStatus) {
                // Close the modal when succeeded
                handleDialogClose(false);
              } else {
                // Test connection when not succeeded
                setIsLocalTestingConnection(true);
                void refetchStatus();
              }
            }}
            className="vai-bg-sapphire-600 hover:vai-bg-sapphire-700"
            disabled={buttonConfig.disabled}
          >
            {buttonConfig.text}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
